// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {useWindowSize} from '@wandb/weave/common/hooks/useWindowSize';
import {TimeDelta} from '@wandb/weave/common/util/time';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import {RunState} from '@wandb/weave/components/RunState';
import {ApolloQueryResult} from 'apollo-client';
import React, {useCallback, useEffect, useState} from 'react';
// @todo: Migrate to 'Link' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Link, useHistory} from 'react-router-dom';
import TimeAgo from 'react-timeago';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Dropdown, Image} from 'semantic-ui-react';

import emptyImg2x from '../assets/il-no-sweeps@2x.png';
import * as Generated from '../generated/graphql';
import {SweepData} from '../state/graphql/sweepPageQuery';
import DOC_URLS from '../util/doc_urls';
import {
  extractErrorMessageFromApolloError,
  propagateErrorsContext,
} from '../util/errors';
import {getSweepDisplayName} from '../util/getSweepDisplayName';
import {
  SweepState,
  sweepStateDescription,
  sweepStateDisplayName,
} from '../util/sweeps';
import {
  launchOverview,
  sweep as sweepUrl,
  sweepControls as sweepControlsUrl,
} from '../util/urls';
import BlankPage from './BlankPage';
import DeleteSweepModal from './DeleteSweepModal';
import {EditableLabel} from './elements/EditableLabel';
import MultiSelectTable from './MultiSelectTable';
import {MultiStateCheckbox} from './MultiStateCheckbox';
import {InstrumentedCenteredWaveLoader as Loader} from './utility/InstrumentedLoader';

type SortBy =
  | 'displayName'
  | 'state'
  | 'queue'
  | 'createdAt'
  | 'user'
  | 'runCount'
  | 'runCountExpected'
  | 'runTime';
type SortFn = (r: SweepData) => any;

const SORT_FUNCTION_BY_NAME: {[s in SortBy]: SortFn} = {
  displayName: r => r.displayName,
  state: r => r.state,
  queue: r =>
    r.scheduler ? (r.scheduler.queue ?? '') : -new Date(r.createdAt).valueOf(),
  createdAt: r => -new Date(r.createdAt).valueOf(),
  user: r => r.user?.username,
  runCount: r => r.runCount,
  runCountExpected: r => r.runCountExpected,
  runTime: r => r.runTime,
};

const SweepsTable = React.memo(
  (props: {entityName: string; projectName: string}) => {
    const {entityName, projectName} = props;
    const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
    const history = useHistory();
    const {responsiveSize} = useWindowSize();
    const isSmallScreen = responsiveSize === 'small';

    const [sortBy, setSortBy] = useState<SortBy>('createdAt');
    const [sortReverse, setSortReverse] = useState(false);
    const toggleSort = useCallback(
      (sb: SortBy) => {
        if (sb === sortBy) {
          setSortReverse(prev => !prev);
          return;
        }
        setSortBy(sb);
        setSortReverse(false);
      },
      [sortBy]
    );
    const renderSortIcon = (sortKey: string) => {
      if (sortBy !== sortKey) {
        return null;
      }
      return (
        <Icon
          name={`sort-${sortReverse ? 'descending' : 'ascending'}`}
          className="report-table__sort-icon"
        />
      );
    };

    const {
      data: sweepsQueryData,
      loading: sweepsQueryLoading,
      error: sweepsQueryError,
      refetch,
    } = Generated.useSweepsQuery({
      variables: {
        entityName,
        projectName,
      },
      context: propagateErrorsContext(),
      fetchPolicy: 'cache-and-network',
    });
    if (sweepsQueryLoading) {
      return <Loader name="sweeps-query-loader" size="huge" />;
    }
    if (!sweepsQueryData) {
      return (
        <div style={{padding: '1rem', fontSize: '1.2rem', fontWeight: 500}}>
          Error fetching sweeps data:{' '}
          {extractErrorMessageFromApolloError(sweepsQueryError)}.
        </div>
      );
    }
    const {project} = sweepsQueryData;

    const readOnly = project?.readOnly;
    const sweeps = project?.sweeps?.edges
      .map(e => e.node)
      .map(s => ({...s, createdAt: s.createdAt.toLocaleString()}));
    const showEstimateColumn = !!sweeps?.filter(x => x.runCountExpected != null)
      ?.length;

    const showLaunch = sweeps?.some(x => x.scheduler != null);

    return (
      <div>
        <MultiSelectTable
          className="sweeps-table"
          readOnly={readOnly || false}
          rows={sweeps ?? []}
          sortBy={SORT_FUNCTION_BY_NAME[sortBy]}
          sortReverse={sortReverse}
          renderHeader={(checked, onClick) => (
            <>
              <div className="report-table__cell report-table__cell--select sweep-col-select">
                <MultiStateCheckbox checked={checked} onClick={onClick} />
              </div>
              <div
                className="report-table__cell report-table__table-title sweep-col-name"
                onClick={() => toggleSort('displayName')}>
                Sweep
                {renderSortIcon('displayName')}
              </div>
              <div
                className="report-table__cell sweep-col-state"
                onClick={() => toggleSort('state')}>
                State
                {renderSortIcon('state')}
              </div>
              {showLaunch && (
                <div
                  className="report-table__cell sweep-col-run-queue"
                  onClick={() => toggleSort('queue')}>
                  Launch queue
                  {renderSortIcon('queue')}
                </div>
              )}
              <div
                className="report-table__cell sweep-col-created"
                onClick={() => toggleSort('createdAt')}>
                Created
                {renderSortIcon('createdAt')}
              </div>
              <div
                className="report-table__cell sweep-col-creator"
                onClick={() => toggleSort('user')}>
                Creator
                {renderSortIcon('user')}
              </div>
              <div
                className="report-table__cell sweep-col-run-count"
                onClick={() => toggleSort('runCount')}>
                Run count
                {renderSortIcon('runCount')}
              </div>
              {showEstimateColumn && (
                <div
                  className="report-table__cell sweep-col-run-count"
                  onClick={() => toggleSort('runCountExpected')}>
                  Est. runs
                  {renderSortIcon('runCountExpected')}
                </div>
              )}
              <div
                className="report-table__cell sweep-col-compute-time"
                onClick={() => toggleSort('runTime')}>
                Compute time
                {renderSortIcon('runTime')}
              </div>
              <div className="report-table__cell sweep-col-menu" />
            </>
          )}
          renderRow={(r, selected, setSelected) => (
            <SweepsTableRow
              key={r.id}
              entityName={entityName}
              projectName={projectName}
              sweep={r}
              readOnly={readOnly || false}
              refetchSweeps={refetch}
              selected={selected}
              setSelected={setSelected}
              showLaunch={showLaunch || false}
              showEstimateColumn={showEstimateColumn}
            />
          )}
          renderDialogs={filteredSelectedSweeps => (
            <DeleteSweepModal
              open={deleteConfirmModalOpen}
              sweeps={filteredSelectedSweeps}
              onClose={() => setDeleteConfirmModalOpen(false)}
              onDelete={() => {
                refetch();
                setDeleteConfirmModalOpen(false);
              }}
            />
          )}
          renderAddButton={() =>
            !sweeps?.length ? (
              <></>
            ) : (
              <Button
                style={{marginLeft: isSmallScreen ? 0 : 12}}
                onClick={() =>
                  history.push(`/${entityName}/${projectName}/create-sweep`)
                }>
                Create Sweep
              </Button>
            )
          }
          renderDeleteButton={() => (
            <Button
              variant="destructive"
              onClick={() => setDeleteConfirmModalOpen(true)}>
              Delete sweeps
            </Button>
          )}
          renderEmpty={
            !sweeps?.length
              ? () => (
                  <NoSweepsMessage
                    entityName={entityName}
                    projectName={projectName}
                  />
                )
              : undefined
          }
        />
      </div>
    );
  }
);
SweepsTable.displayName = 'SweepsTable';

export default SweepsTable;

interface SweepsTableSweepProps {
  entityName: string;
  projectName: string;
  sweep: SweepData;
  readOnly: boolean;
  selected: boolean;
  refetchSweeps: (
    variables?:
      | {
          entityName: string;
          projectName: string;
        }
      | undefined
  ) => Promise<ApolloQueryResult<any>>;
  showLaunch: boolean;
  showEstimateColumn: boolean;
  setSelected(id: string, selected: boolean): void;
}

const SweepsTableRow: React.FC<SweepsTableSweepProps> = React.memo(props => {
  const {
    entityName,
    projectName,
    sweep,
    readOnly,
    refetchSweeps,
    selected,
    setSelected,
    showLaunch,
    showEstimateColumn,
  } = props;
  const sweepDisplayName = getSweepDisplayName(sweep) || 'Sweep';
  const [editingName, setEditingName] = React.useState(false);
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [upsertSweep] = Generated.useUpsertSweepMutation();

  const urlParams = {
    entityName,
    projectName,
    sweepName: sweep.name,
  };
  const sweepLink = sweepUrl(urlParams);
  const sweepControlsLink = sweepControlsUrl(urlParams);

  const onSelectChanged = useCallback(
    () => setSelected(sweep.id, !selected),
    [sweep.id, selected, setSelected]
  );

  const launchScheduler =
    sweep.scheduler != null ? JSON.parse(sweep.scheduler) : null;

  return (
    <div className="report-table__row">
      <DeleteSweepModal
        open={deleteConfirmModalOpen}
        sweeps={[sweep]}
        onClose={() => setDeleteConfirmModalOpen(false)}
        onDelete={() => {
          refetchSweeps();
          setDeleteConfirmModalOpen(false);
        }}
      />
      <div className="report-table__cell report-table__cell--select sweep-col-select">
        <MultiStateCheckbox
          checked={selected ? 'checked' : 'unchecked'}
          onClick={onSelectChanged}
        />
      </div>
      <div className="report-table__cell report-table__cell--title sweep-col-name">
        <Link to={sweepLink}>
          <Icon
            style={{marginRight: 12, marginLeft: 6}}
            name="sweeps-broom-alt"
          />
          <EditableLabel
            key={sweep.id}
            title={sweepDisplayName}
            onSave={(displayName: string) => {
              upsertSweep({
                variables: {id: sweep.id, displayName},
              }).then(() => {
                refetchSweeps();
              });
            }}
            onBlur={() => setEditingName(false)}
            editing={editingName}
            serverText={sweepDisplayName}
            onClick={() => false} // we use the menu option to trigger editing mode, rather than direct click
          />
        </Link>
      </div>
      <div className="report-table__cell sweep-col-state">
        <Link
          title={sweepStateDescription(sweep.state as SweepState)}
          to={sweepControlsLink}>
          <RunState
            value={sweepStateDisplayName(
              sweep.state as SweepState
            ).toLowerCase()}
          />
        </Link>
      </div>
      {showLaunch && (
        <div className="report-table__cell sweep-col-run-queue">
          <Link title="Launch run queue" to={launchOverview()}>
            {launchScheduler?.queue ?? ''}
          </Link>
        </div>
      )}
      <div className="report-table__cell sweep-col-created">
        <Link to={sweepLink}>
          <TimeAgo date={sweep.createdAt} live={false} />
        </Link>
      </div>
      <div className="report-table__cell sweep-col-creator">
        <Link to={sweepLink}>
          <Image
            src={sweep?.user?.photoUrl}
            avatar
            onError={(i: any) => (i.target.style.display = 'none')}
          />
          {sweep?.user?.username}
        </Link>
      </div>
      <div className="report-table__cell sweep-col-run-count">
        <Link to={sweepLink}>{sweep.runCount}</Link>
      </div>
      {showEstimateColumn && (
        <div className="report-table__cell sweep-col-run-count">
          <Link to={sweepLink}>{sweep.runCountExpected}</Link>
        </div>
      )}
      <div className="report-table__cell sweep-col-compute-time">
        <Link to={sweepLink}>
          {new TimeDelta(sweep.runTime).toSingleUnitString()}
        </Link>
      </div>
      <div className="report-table__cell sweep-col-menu">
        <Dropdown
          className="dropdown-menu-icon-button"
          pointing="top right"
          trigger={
            <LegacyWBIcon
              className="report-table__action-button"
              name="overflow"
              onClick={(e: React.SyntheticEvent) => {
                e.preventDefault();
              }}
            />
          }>
          <Dropdown.Menu>
            {!readOnly && (
              <Dropdown.Item
                icon={<LegacyWBIcon name="edit" />}
                content="Rename sweep"
                onClick={(e: React.SyntheticEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setEditingName(true);
                }}
              />
            )}
            {!readOnly && (
              <Dropdown.Item
                icon="wbic-ic-delete"
                content="Delete sweep"
                disabled={readOnly}
                onClick={(e: React.SyntheticEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDeleteConfirmModalOpen(true);
                }}
              />
            )}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
  );
});

type NoSweepsMessageProps = {
  entityName: string;
  projectName: string;
};

const NoSweepsMessage: React.FC<NoSweepsMessageProps> = React.memo(
  ({entityName, projectName}) => {
    const history = useHistory();
    useEffect(() => {
      window.analytics?.track('Empty State Viewed', {location: 'sweeps'});
    }, []);
    return (
      <BlankPage
        header="Sweeps"
        subheader="Scalable, customizable hyperparameter search"
        info="Use Sweeps for hyperparameter optimization. Sweeps work with Bayesian search, random search, grid search, custom logic, and also support early stopping."
        graphics={[
          {
            src: emptyImg2x,
            alt: 'sweeps-example',
          },
        ]}
        secondaryAction={{
          content: 'Try Code',
          href: 'http://wandb.me/sweeps-colab',
          onClick: () => {
            window.analytics?.track('See Example Clicked', {
              location: 'empty sweeps tab',
            });
            window.analytics?.track('Try Code Clicked', {
              location: 'sweeps empty tab',
            });
          },
        }}
        primaryAction={{
          content: 'Create Sweep',
          onClick: () => {
            history.push(`/${entityName}/${projectName}/create-sweep`);
            window.analytics?.track('Create Sweep Clicked', {
              entityName,
              projectName,
              location: 'empty sweeps tab',
            });
          },
        }}
        docLink={{
          href: DOC_URLS.sweeps,
          onClick: () => {
            window.analytics?.track('See Docs Clicked', {
              location: 'empty sweeps tab',
            });
            window.analytics?.track('See Docs Clicked', {
              location: 'sweeps empty tab',
            });
          },
        }}
      />
    );
  }
);
